import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, HStack, Spinner, Table, Text, VStack } from "@chakra-ui/react";
import { HiChevronDown, HiChevronRight, HiExclamationTriangle } from "react-icons/hi2";
import { CandidateService } from "@/services/CandidateService";
import { ScoreBadge, evaluationStatusFromApi, aiMatchLevelFromApi, EVALUATION_STATUS } from "@/scoringUi";

const AI_LEVEL_LABELS = {
    FULL_MATCH: "Pełna zgodność",
    HIGH_MATCH: "Wysoka zgodność",
    PARTIAL_MATCH: "Częściowa zgodność",
    LOW_MATCH: "Niska zgodność",
    NO_MATCH: "Brak zgodności",
    UNKNOWN: "Nieznany",
};

/**
 * "Ocena" tab (spec 2 §9-11): the client's sole view of a candidate's score
 * -- reads GET /api/candidate/{id}/evaluation as-is, never recomputes
 * anything (AC-FE-14). No scenario-vs-recruitment split is shown here (spec
 * §12); that detail is only in the raw `source` field per breakdown row,
 * exposed here as a small technical label, not a primary UI element.
 */
const CandidateEvaluationTab = ({ candidateId }) => {
    const { t } = useTranslation("models");
    const [evaluation, setEvaluation] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isRecalculating, setIsRecalculating] = useState(false);
    const [expandedKey, setExpandedKey] = useState(null);

    const fetchEvaluation = () => {
        setIsLoading(true);
        setHasError(false);
        CandidateService.getEvaluation(candidateId)
            .then(setEvaluation)
            .catch((err) => {
                if (err?.response?.status === 404) {
                    setEvaluation(null);
                } else {
                    setHasError(true);
                }
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        fetchEvaluation();
    }, [candidateId]);

    const handleRecalculate = async () => {
        setIsRecalculating(true);
        try {
            const updated = await CandidateService.recalculateEvaluation(candidateId);
            setEvaluation(updated);
        } catch {
            setHasError(true);
        } finally {
            setIsRecalculating(false);
        }
    };

    if (isLoading) {
        return (
            <VStack w="full" py={12} align="center">
                <Spinner color="brand.400" size="lg" />
            </VStack>
        );
    }

    if (hasError) {
        return (
            <Box bg="bg.card" border="1px solid" borderColor="red.700" borderRadius={12} p={5}>
                <Text color="red.400" fontSize="sm">{t("candidateDetailPage.evaluationLoadError")}</Text>
            </Box>
        );
    }

    const status = evaluation ? evaluationStatusFromApi(evaluation.status) : null;

    // Distinct from "0 punktów": no CandidateEvaluation row exists yet, or the
    // conversation hasn't produced enough data (IN_PROGRESS). Must never look
    // like a scored 0 -- spec §29-equivalent requirement (§13 UI states).
    if (!evaluation || status === EVALUATION_STATUS.IN_PROGRESS) {
        return (
            <VStack w="full" bg="bg.card" border="1px dashed" borderColor="border.secondary" borderRadius={12} p={8} gap={2}>
                <Text color="text.grey" fontSize="sm" fontStyle="italic">
                    {t("candidateDetailPage.evaluationNotAvailable")}
                </Text>
                <Button size="sm" variant="outlined" onClick={handleRecalculate} loading={isRecalculating}>
                    {t("candidateDetailPage.evaluationRecalculate")}
                </Button>
            </VStack>
        );
    }

    const isDisqualified = status === EVALUATION_STATUS.DISQUALIFIED;
    const isNeedsReview = status === EVALUATION_STATUS.NEEDS_REVIEW;

    return (
        <VStack gap={4} align="stretch">
            {/* Header: score, status, completeness */}
            <Box bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                <HStack justify="space-between" flexWrap="wrap" gap={4}>
                    <HStack gap={6}>
                        <VStack align="flex-start" gap={1}>
                            <Text color="text.grey" fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                                {t("candidateDetailPage.evaluationScore")}
                            </Text>
                            <Text color="white" fontWeight={700} fontSize="2xl">
                                {evaluation.scoreRaw} / {evaluation.scoreMax}
                                <Text as="span" color="text.grey" fontSize="sm" fontWeight={400}> ({Math.round(evaluation.scorePercent)}%)</Text>
                            </Text>
                        </VStack>
                        <VStack align="flex-start" gap={1}>
                            <Text color="text.grey" fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                                {t("candidateDetailPage.evaluationStatus")}
                            </Text>
                            <ScoreBadge evaluation={{ scorePercent: evaluation.scorePercent, status }} size="md" />
                        </VStack>
                        <VStack align="flex-start" gap={1}>
                            <Text color="text.grey" fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                                {t("candidateDetailPage.evaluationCompleteness")}
                            </Text>
                            <Text color="white" fontWeight={600} fontSize="lg">{Math.round(evaluation.completeness * 100)}%</Text>
                        </VStack>
                    </HStack>
                    <Button size="sm" variant="outlined" onClick={handleRecalculate} loading={isRecalculating}>
                        {t("candidateDetailPage.evaluationRecalculate")}
                    </Button>
                </HStack>
            </Box>

            {/* Disqualification banner -- visually dominant, never the same as a low score (spec §11/§29). */}
            {isDisqualified && (
                <HStack bg="red.950" border="1px solid" borderColor="red.700" borderRadius={12} p={4} gap={3} align="flex-start">
                    <Box color="red.400" fontSize="xl" flexShrink={0}><HiExclamationTriangle /></Box>
                    <VStack align="flex-start" gap={1}>
                        <Text color="red.300" fontWeight={700} fontSize="sm">{t("candidateDetailPage.evaluationDisqualified")}</Text>
                        {evaluation.disqualificationReasons?.length > 0 ? (
                            evaluation.disqualificationReasons.map((reason, i) => (
                                <Text key={i} color="red.200" fontSize="sm">• {reason}</Text>
                            ))
                        ) : (
                            <Text color="red.200" fontSize="sm">{t("candidateDetailPage.evaluationNoReasonGiven")}</Text>
                        )}
                    </VStack>
                </HStack>
            )}

            {isNeedsReview && !isDisqualified && (
                <HStack bg="bg.secondary" border="1px solid" borderColor="border.primary" borderRadius={12} p={4} gap={3}>
                    <Box color="text.grey" fontSize="lg" flexShrink={0}><HiExclamationTriangle /></Box>
                    <Text color="text.grey" fontSize="sm">{t("candidateDetailPage.evaluationNeedsReview")}</Text>
                </HStack>
            )}

            {/* Breakdown table (spec §9) */}
            <Box bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} overflowX="auto">
                <Table.Root size="sm" minW="600px">
                    <Table.Header>
                        <Table.Row bg="bg.dark">
                            <Table.ColumnHeader color="text.grey" fontSize="xs" textTransform="uppercase">{t("candidateDetailPage.evaluationCriterion")}</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.grey" fontSize="xs" textTransform="uppercase">{t("candidateDetailPage.evaluationValue")}</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.grey" fontSize="xs" textTransform="uppercase">{t("candidateDetailPage.evaluationMatchedRule")}</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.grey" fontSize="xs" textTransform="uppercase" textAlign="right">{t("candidateDetailPage.evaluationPoints")}</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.grey" fontSize="xs" textTransform="uppercase" textAlign="right">{t("candidateDetailPage.evaluationMax")}</Table.ColumnHeader>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {evaluation.criterionResults.map((row) => {
                            const isAiMatch = row.scoringType === "AiMatch";
                            const isExpanded = expandedKey === row.criterionKey;
                            return (
                                <Fragment key={row.criterionKey}>
                                    <Table.Row
                                        bg={row.isDisqualifying ? "red.950" : "transparent"}
                                        cursor={isAiMatch ? "pointer" : "default"}
                                        onClick={() => isAiMatch && setExpandedKey(isExpanded ? null : row.criterionKey)}
                                    >
                                        <Table.Cell color="white" fontSize="sm">
                                            <HStack gap={1}>
                                                {isAiMatch && (isExpanded ? <HiChevronDown /> : <HiChevronRight />)}
                                                <Text>{row.criterionName}</Text>
                                                {row.isDisqualifying && <Text color="red.400" fontSize="xs">({t("candidateDetailPage.evaluationDisqualifies")})</Text>}
                                            </HStack>
                                        </Table.Cell>
                                        <Table.Cell color="text.grey" fontSize="sm">{row.capturedValue ?? "—"}</Table.Cell>
                                        <Table.Cell color="text.grey" fontSize="sm">{row.matchedRule ?? "—"}</Table.Cell>
                                        <Table.Cell color="white" fontSize="sm" fontWeight={600} textAlign="right">{row.points}</Table.Cell>
                                        <Table.Cell color="text.grey" fontSize="sm" textAlign="right">{row.maxScore}</Table.Cell>
                                    </Table.Row>
                                    {isAiMatch && isExpanded && (
                                        <Table.Row key={`${row.criterionKey}-detail`} bg="bg.dark">
                                            <Table.Cell colSpan={5} py={3}>
                                                <VStack align="flex-start" gap={2} px={2}>
                                                    <Text color="text.grey" fontSize="xs">
                                                        {t("candidateDetailPage.evaluationAiLevel")}: <Text as="span" color="white">{AI_LEVEL_LABELS[aiMatchLevelFromApi(row.aiLevel)] ?? row.aiLevel ?? "—"}</Text>
                                                    </Text>
                                                    {row.aiConfidence != null && (
                                                        <Text color="text.grey" fontSize="xs">
                                                            {t("candidateDetailPage.evaluationAiConfidence")}: <Text as="span" color="white">{Math.round(row.aiConfidence * 100)}%</Text>
                                                        </Text>
                                                    )}
                                                    {row.aiReason && (
                                                        <Text color="text.grey" fontSize="xs">
                                                            {t("candidateDetailPage.evaluationAiReason")}: <Text as="span" color="white">{row.aiReason}</Text>
                                                        </Text>
                                                    )}
                                                    {row.aiEvidence && (
                                                        <Text color="text.grey" fontSize="xs">
                                                            {t("candidateDetailPage.evaluationAiEvidence")}: <Text as="span" color="white">{row.aiEvidence}</Text>
                                                        </Text>
                                                    )}
                                                </VStack>
                                            </Table.Cell>
                                        </Table.Row>
                                    )}
                                </Fragment>
                            );
                        })}
                    </Table.Body>
                </Table.Root>
            </Box>
        </VStack>
    );
};

export default CandidateEvaluationTab;
